/**
 * Username Validation Utilities
 * 
 * Centralized username validation and normalization for GENI.
 * Used by: Settings, Signup, ShareModal, publicUrl helpers
 * 
 * USERNAME RULES:
 * - 3-30 characters
 * - Lowercase letters, numbers, underscores, hyphens only
 * - Must start with a letter
 * - No spaces
 * - Case-insensitive (stored as entered, looked up case-insensitively)
 * - Unique per user
 */

// Username constraints
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;

// Regex pattern: starts with letter, only lowercase letters, numbers, underscores, hyphens
// Pattern allows mixed case input but validation normalizes to lowercase for comparison
export const USERNAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

// Reserved usernames that cannot be used (system routes, common terms)
export const RESERVED_USERNAMES = [
  'admin', 'administrator', 'root', 'system', 'geni', 'support', 'help',
  'api', 'app', 'www', 'mail', 'email', 'user', 'users', 'profile', 'profiles',
  'settings', 'login', 'logout', 'signup', 'register', 'dashboard', 'home',
  'shared', 'share', 'public', 'private', 'me', 'my', 'null', 'undefined',
  'test', 'demo', 'example', 'about', 'contact', 'terms', 'privacy', 'legal'
];

/**
 * Normalize username for storage/comparison
 * - Trims whitespace
 * - Preserves original casing for display
 */
export const normalizeUsername = (username) => {
  if (!username) return '';
  return username.trim();
};

/**
 * Normalize username for comparison (lowercase)
 * Used for uniqueness checks and lookups
 */
export const normalizeUsernameForComparison = (username) => {
  if (!username) return '';
  return username.trim().toLowerCase();
};

/**
 * Validate username and return detailed result
 * 
 * @param {string} username - The username to validate
 * @returns {object} { valid: boolean, error: string | null, normalized: string }
 */
export const validateUsername = (username) => {
  // Null/empty check
  if (!username || username.trim() === '') {
    return { valid: false, error: 'Username is required', normalized: '' };
  }
  
  const normalized = normalizeUsername(username);
  const lowercased = normalized.toLowerCase();
  
  // Length check
  if (normalized.length < USERNAME_MIN_LENGTH) {
    return { 
      valid: false, 
      error: `Username must be at least ${USERNAME_MIN_LENGTH} characters`,
      normalized 
    };
  }
  
  if (normalized.length > USERNAME_MAX_LENGTH) {
    return { 
      valid: false, 
      error: `Username must be ${USERNAME_MAX_LENGTH} characters or less`,
      normalized 
    };
  }
  
  // Pattern check
  if (!USERNAME_PATTERN.test(normalized)) {
    if (/^\d/.test(normalized)) {
      return { 
        valid: false, 
        error: 'Username must start with a letter',
        normalized 
      };
    }
    if (/\s/.test(normalized)) {
      return { 
        valid: false, 
        error: 'Username cannot contain spaces',
        normalized 
      };
    }
    return { 
      valid: false, 
      error: 'Username can only contain letters, numbers, underscores, and hyphens',
      normalized 
    };
  }
  
  // Reserved username check
  if (RESERVED_USERNAMES.includes(lowercased)) {
    return { 
      valid: false, 
      error: 'This username is reserved',
      normalized 
    };
  }
  
  return { valid: true, error: null, normalized };
};

/**
 * Check if a username is valid (simple boolean check)
 */
export const isValidUsername = (username) => {
  return validateUsername(username).valid;
};

/**
 * Format username for URL (lowercase for consistency in URLs)
 */
export const formatUsernameForUrl = (username) => {
  if (!username) return '';
  return normalizeUsername(username).toLowerCase();
};

export default {
  USERNAME_MIN_LENGTH,
  USERNAME_MAX_LENGTH,
  USERNAME_PATTERN,
  RESERVED_USERNAMES,
  normalizeUsername,
  normalizeUsernameForComparison,
  validateUsername,
  isValidUsername,
  formatUsernameForUrl
};
